import {
    type CalendarEntry,
    type CalendarEventData,
    CalendarFrequency,
    type CalendarList,
    type CalendarRecord,
    FrequencyPluralMap,
    type ResolvedFrequency,
} from "@qendo/calendar";
import type { JournalData } from "@qendo/profile/journal";
import type { ThemeColours } from "@qendo/theme";
import type { TimeZone } from "@qendo/util";
import {
    add,
    addDays,
    compareDesc,
    type DateArg,
    type Duration,
    isFuture,
    isSameDay,
    min,
    startOfDay,
    toDate,
} from "date-fns";
import { Frequency } from "expo-calendar";
import { Markings } from "react-native-calendars/src/calendar/day/marking";
import type { MarkedDates } from "react-native-calendars/src/types";
import type { CurrentCycleData } from "~/entities/profile/current-cycle-data";
import { filled } from "~/util";
import {
    currentTimezone,
    daysForMonth,
    daysForWeek,
    formatDateString,
    fromTimezone,
    isSameDayOrEarlier,
} from "~/util/dates";
import { extractPeriods } from "~/util/qendo/cycle";

export function markDates(
    calendar: CalendarRecord,
    today: DateArg<Date>,
    theme: ThemeColours,
    cycle?: CurrentCycleData | null,
): MarkedDates {
    return Object.fromEntries(
        Object.entries(calendar).map(([d, c]) => {
            const has_flare = c.some((journal) => isJournal(journal) && journal.flare);
            const has_journal = c.some((journal) => isJournal(journal) && !journal.flare);
            const has_event = c.some((event) => isEvent(event));
            const is_marked = has_flare || has_journal || has_event;
            const is_multi = (has_flare && (has_journal || has_event)) || (has_journal && has_event);
            const is_today = isSameDay(d, today);
            const is_future = isFuture(d);
            const { current, future } = cycle ? extractPeriods(cycle, d) : { current: null };
            const marking_type = current ? Markings.CUSTOM : is_multi ? Markings.MULTI_DOT : Markings.DOT;

            const period = future ?? current;

            return [
                d,
                {
                    marked: is_marked,
                    type: marking_type,
                    dots: [
                        has_journal ? { color: theme.tracking.indicator, key: `${d}-marked` } : null,
                        has_flare ? { color: theme.icon.danger, key: `${d}-flare` } : null,
                        has_event ? { color: theme.tracking.info, key: `${d}-event` } : null,
                    ].filter((v) => v !== null),
                    today: is_today,
                    periods:
                        period && isSameDayOrEarlier(d, period.end_at)
                            ? [
                                  {
                                      color:
                                          period.started_at && is_future
                                              ? "transparent"
                                              : theme.tracking["past-period"],
                                      startingDay: isSameDay(d, period.start_at),
                                      endingDay: isSameDay(d, period.end_at),
                                  },
                              ]
                            : undefined,
                },
            ];
        }),
    ) as MarkedDates;
}

export function isJournal(record: CalendarEntry): record is JournalData {
    return Object.hasOwn(record, "recorded_on");
}

export function isEvent(record: CalendarEntry): record is CalendarEventData {
    return Object.hasOwn(record, "start_at");
}

export function sortCalendar(a: CalendarEntry, b: CalendarEntry) {
    return compareDesc(
        (a as JournalData).recorded_on ?? (a as CalendarEventData).start_at,
        (b as JournalData).recorded_on ?? (b as CalendarEventData).start_at,
    );
}

export function convertFrequency<T extends Frequency | CalendarFrequency>(
    frequency: T,
    interval: number,
): { frequency: ResolvedFrequency<T>; interval: number } {
    if (frequency === CalendarFrequency.FORTNIGHTLY) {
        return { frequency: Frequency.WEEKLY as ResolvedFrequency<T>, interval: interval * 2 };
    }

    return { frequency: frequency as ResolvedFrequency<T>, interval };
}

export function filterCalendar<T extends CalendarEntry>(
    entries: CalendarList<T>,
    date: DateArg<Date>,
    search?: string | null | undefined,
    time_zone?: TimeZone,
): CalendarList<T> {
    const target = startOfDay(toDate(date));

    const terms = (search ?? "")
        .trim()
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter(filled);

    const matchesSearch = (words?: string[] | null): boolean => {
        return filled(terms) ? terms.every((term) => filled(words) && words.some((word) => word.includes(term))) : true;
    };

    const tz = time_zone ?? currentTimezone();

    return entries.filter((c, idx, arr) => {
        const compare = isJournal(c) ? toDate(c.recorded_on) : fromTimezone(c.start_at, c.time_zone ?? tz);

        return (
            isSameDay(compare, target) &&
            matchesSearch(c.search) &&
            arr.findIndex((s) => s.id === c.id) === idx &&
            (isJournal(c) || !c.multi_day || c.occurrence !== null)
        );
    });
}

export function filterCalendarForDates<T extends CalendarEntry>(
    entries: CalendarList<T>,
    dates: DateArg<Date>[],
    search?: string | null | undefined,
) {
    return Object.fromEntries(dates.map((day) => [formatDateString(day), filterCalendar(entries, day, search)]));
}

export function filterCalendarForWeek<T extends CalendarEntry>(
    entries: CalendarList<T>,
    date: DateArg<Date>,
    search?: string | null | undefined,
): CalendarRecord<T> {
    return filterCalendarForDates(entries, daysForWeek(date), search);
}

export function filterCalendarForMonth<T extends CalendarEntry>(
    entries: CalendarList<T>,
    date: DateArg<Date>,
    search?: string | null | undefined,
): CalendarRecord<T> {
    return filterCalendarForDates(entries, daysForMonth(date), search);
}

export function getEndDate(event: CalendarEventData): Date | null {
    if (event.recurrence) {
        if (event.recurrence.end_date || event.recurrence.occurrence) {
            const occurrences = resolveRecurrences(event);

            return occurrences[occurrences.length - 1];
        }

        return null;
    }

    return fromTimezone(event.end_at ?? event.start_at, event.time_zone);
}

export function resolveRecurrences(
    event: CalendarEventData,
    until?: DateArg<Date> | null,
    limit?: number | null,
): Date[] {
    const events = [];
    let next_event = startOfDay(fromTimezone(event.start_at, event.time_zone));

    if (event.recurrence) {
        const end_at =
            until && event.recurrence?.end_date
                ? min([until, event.recurrence.end_date])
                : (until ?? event.recurrence?.end_date);
        const repeat =
            limit && event.recurrence?.occurrence
                ? Math.min(limit, event.recurrence?.occurrence)
                : (limit ?? event.recurrence?.occurrence ?? 20);
        let count = 0;

        const { frequency, interval } = convertFrequency(event.recurrence.frequency, event.recurrence.interval);
        const duration: Duration = { [FrequencyPluralMap[frequency]]: interval };

        while (end_at ? isSameDayOrEarlier(next_event, end_at) : count < repeat) {
            events.push(next_event);
            next_event = add(next_event, duration);
            count++;
        }
    } else {
        const end_at = fromTimezone(event.end_at ?? event.start_at, event.time_zone);

        while (isSameDayOrEarlier(next_event, end_at)) {
            events.push(next_event);
            next_event = addDays(next_event, 1);
        }
    }

    return events;
}
